// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BrowserRouter as Router, Routes, Route, useParams, useLocation, useNavigate } from 'react-router-dom';

import Header from './components/Header';
import SearchBar from './components/SearchBar';
import VideoGrid from './components/VideoGrid';
import VideoModal from './components/VideoModal';
import AuthForm from './components/AuthForm';
import UserProfile from './components/UserProfile';
import SharedListPage from './components/SharedListPage';
import FindRestaurantCard from './components/FindRestaurantCard';
import VideoMap from './components/VideoMap';
import ExtractionFixDialog from './components/ExtractionFixDialog';
import SettingsDrawer from './components/SettingsDrawer';
import BottomTabs from './components/BottomTabs';
import PasteFab from './components/PasteFab';
import DesktopShell from './components/DesktopShell';
import DetailsDrawer from './components/DetailsDrawer';
import ListsHub from './components/ListsHub';
import ProfilePage from './components/ProfilePage';
import OnboardingCoach from './components/OnboardingCoach';
import AddLinkPrompt from './components/AddLinkPrompt';
import BulkAddDialog from './components/BulkAddDialog';
import SupabaseDiagnostics from './components/SupabaseDiagnostics';
import DiagnosticsBanner from './components/DiagnosticsBanner';
import ConfirmPlaceSheet from './components/ConfirmPlaceSheet';
import ConfirmExtractDialog from './components/ConfirmExtractDialog';
import BulkProgressDrawer, { ProgressItem } from './components/BulkProgressDrawer';
import AdminDashboard from './admin/AdminDashboard';
import { StepBar } from './components/StepBar';

import { ToastProvider, useToast } from './components/ToastProvider';
import { SettingsProvider, useSettings } from './components/SettingsContext';

import { supabase } from './utils/supabaseClient';
import { extractVideoData } from './utils/openaiService';
import { enrichVideoWithPlaces } from './lib/maps/enrichVideo';
import useAddVideo from './hooks/useAddVideo';
import type { OEmbedRequiredError, RankedCandidate } from './types/api';

import { Video, SearchFilters, UserProfile as UserProfileType } from './types';

// Wrapper for /list/:listId route
const SharedListPageWrapper: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!listId) return <div className="text-center text-red-500 text-xl mt-20">Lista no encontrada.</div>;

  return (
    <>
      <SharedListPage listId={listId} onVideoClick={(v) => { setSelectedVideo(v); setIsModalOpen(true); }} />
      <VideoModal video={selectedVideo} isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setSelectedVideo(null); }} />
    </>
  );
};

const MainApp: React.FC = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [filteredVideos, setFilteredVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [session, setSession] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);
  const [showProfile, setShowProfile] = useState(false);

  // New video handling with oEmbed gate and confirmation flow
  const { loading: addVideoLoading, oembedError, confirm, error: addVideoError, addVideo, reset: resetAddVideo } = useAddVideo();

  // User location for Places bias
  const [userLoc, setUserLoc] = useState<{ latitude: number; longitude: number } | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({ platform: 'all', cuisine: 'all', location: '' });
  // Persist filters/search in query string when on /map
  const location = useLocation();
  const navigate = useNavigate();
  // On mount, hydrate from query
  useEffect(() => {
    if (location.pathname !== '/map') return;
    const params = new URLSearchParams(location.search);
    const term = params.get('q') || '';
    const platform = (params.get('platform') as any) || 'all';
    const cuisine = params.get('cuisine') || 'all';
    const loc = params.get('loc') || '';
    setSearchTerm(term);
    setFilters({ platform, cuisine, location: loc });
  }, []);
  // When filters or search change on /map, update URL
  useEffect(() => {
    if (location.pathname !== '/map') return;
    const params = new URLSearchParams();
    if (searchTerm) params.set('q', searchTerm);
    if (filters.platform !== 'all') params.set('platform', filters.platform);
    if (filters.cuisine !== 'all') params.set('cuisine', filters.cuisine);
    if (filters.location) params.set('loc', filters.location);
    navigate({ pathname: '/map', search: params.toString() }, { replace: true });
  }, [searchTerm, filters]);
  const [highlightedVideoId, setHighlightedVideoId] = useState<string | null>(null);

  const itemRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const enrichAbortRef = useRef<AbortController | null>(null);

  const [fixOpen, setFixOpen] = useState(false);
  const [fixUrl, setFixUrl] = useState('');
  const [fixError, setFixError] = useState<string | undefined>(undefined);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  // Add-video UX state
  const [clipboardUrl, setClipboardUrl] = useState<string>('');
  const [showPastePrompt, setShowPastePrompt] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDraft, setConfirmDraft] = useState<any>(null);
  const [bulkProgressOpen, setBulkProgressOpen] = useState(false);
  const [bulkItems, setBulkItems] = useState<ProgressItem[]>([]);
  const localStubIdsRef = useRef<Set<string>>(new Set());

  // Inline add progress overlay (single add only)
  const [addingActive, setAddingActive] = useState(false);
  const [addingProgress, setAddingProgress] = useState(0);
  const [addingText, setAddingText] = useState<string>('');
  


  const { toast: enqueueToast } = useToast();

  // --- DB → UI normalization helpers ---
  const toNumber = (v: any, fallback = 0): number => {
    const n = typeof v === 'number' ? v : Number(String(v || '').replace(/[,\s]/g, ''));
    return Number.isFinite(n) ? n : fallback;
  };
  const parseJsonArray = (v: any): string[] | null => {
    try {
      if (Array.isArray(v)) return v.map((x) => String(x));
      if (v == null) return null;
      const s = String(v).trim();
      if (!s) return null;
      // Accept JSON arrays and Postgres text arrays already serialized as JSON strings
      if (s.startsWith('[') && s.endsWith(']')) {
        const arr = JSON.parse(s);
        return Array.isArray(arr) ? arr.map((x: any) => String(x)) : null;
      }
      // Fallback: split by newline if it looks like lines
      if (s.includes('\n') || s.includes('),')) {
        return s.split(/\n|\),\s*/g).map((t) => t.replace(/^[^:]+:\s*/,'').trim()).filter(Boolean);
      }
      return null;
    } catch { return null; }
  };
  const parseJsonArrayLoose = (v: any): string[] => parseJsonArray(v) || [];
  const parseJsonObj = <T,>(v: any): T | undefined => {
    try {
      if (v && typeof v === 'object') return v as T;
      if (typeof v === 'string') {
        const s = v.trim();
        if (s.startsWith('{') || s.startsWith('[')) return JSON.parse(s) as T;
      }
    } catch {}
    return undefined;
  };
  const normalizePlatform = (p: any): any => {
    const s = String(p || '').trim();
    const allowed = ['YouTube', 'TikTok', 'Instagram', 'Facebook', 'Other'];
    return allowed.includes(s) ? (s as any) : 'Other';
  };
  const normalizeFromDb = (row: any): Video => {
    const openingHours = parseJsonArray(row.opening_hours_weekday_text);
    return {
      platform: normalizePlatform(row.platform),
      video_url: String(row.video_url || ''),
      thumbnail_url: row.thumbnail_url ?? 'N/A',
      title: String(row.title || 'N/A'),
      creator_name: String(row.creator_name || 'N/A'),
      views: toNumber(row.views),
      likes: toNumber(row.likes),
      embed_html: row.embed_html ?? null,
      restaurant_name: String(row.restaurant_name || 'N/A'),
      restaurant_cuisine: String(row.restaurant_cuisine || 'N/A'),
      restaurant_location: String(row.restaurant_location || 'N/A'),
      restaurant_address: String(row.restaurant_address || 'N/A'),
      restaurant_rating: toNumber(row.restaurant_rating),
      restaurant_price_range: row.restaurant_price_range ?? null,
      google_maps_url: String(row.google_maps_url || 'N/A'),
      extracted_dish_name: String(row.extracted_dish_name || 'N/A'),
      extracted_ingredients: parseJsonArrayLoose(row.extracted_ingredients),
      extracted_estimated_price: toNumber(row.extracted_estimated_price),
      id: row.id ? String(row.id) : undefined,
      user_id: row.user_id ? String(row.user_id) : undefined,
      created_at: row.created_at ? String(row.created_at) : undefined,
      photo_attribution_html: row.photo_attribution_html ?? null,
      provider_thumbnail_url: row.provider_thumbnail_url ?? null,
      places_photo_url: row.places_photo_url ?? null,
      open_now: typeof row.open_now === 'boolean' ? row.open_now : null,
      opening_hours_weekday_text: openingHours,
      reviews: (parseJsonObj<any[]>(row.reviews) || (Array.isArray(row.reviews) ? row.reviews : undefined)),
    };
  };

  // Remove transient/internal fields before DB insert
  function normalizePriceRange(input: unknown): string | null {
    if (input === null || input === undefined) return null;
    if (typeof input === 'number' && Number.isFinite(input)) {
      const n = Math.round(input);
      if (n >= 1 && n <= 4) return '$'.repeat(n);
      return null;
    }
    if (typeof input === 'string') {
      const s = input.trim();
      if (/^\$+$/.test(s)) {
        const len = Math.max(1, Math.min(4, s.length));
        return '$'.repeat(len);
      }
      if (/^€+$/.test(s)) {
        const len = Math.max(1, Math.min(4, s.length));
        return '$'.repeat(len);
      }
      const n = parseInt(s, 10);
      if (Number.isFinite(n) && n >= 1 && n <= 4) return '$'.repeat(n);
      const lower = s.toLowerCase();
      if (['cheap', 'inexpensive'].includes(lower)) return '$';
      if (['moderate', 'medium'].includes(lower)) return '$$';
      if (['expensive'].includes(lower)) return '$$$';
      if (['very expensive', 'luxury'].includes(lower)) return '$$$$';
      return null;
    }
    return null;
  }

  // Helper to safely handle restaurant names with quotes and special characters
  function sanitizeRestaurantName(name: string): string {
    if (!name || name === 'N/A') return name;
    
    // Preserve quotes but normalize them to standard double quotes
    return name
      .replace(/["'"`'']/g, '"')
      // Clean up excessive whitespace
      .replace(/\s+/g, ' ')
      .trim();
  }

  function sanitizeForInsert<T extends Record<string, any>>(obj: T): T {
    const out: any = { ...obj };
    for (const k of Object.keys(out)) {
      if (k.startsWith('_')) delete out[k];
    }
    if ('restaurant_price_range' in out) {
      const norm = normalizePriceRange(out.restaurant_price_range);
      out.restaurant_price_range = norm;
    }
    // Safely handle restaurant names with quotes
    if ('restaurant_name' in out && typeof out.restaurant_name === 'string') {
      out.restaurant_name = sanitizeRestaurantName(out.restaurant_name);
    }
    return out;
  }

  // Session
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => { if (mounted) setSession(data?.session ?? null); });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => { if (mounted) setSession(s ?? null); });
    return () => { mounted = false; listener?.subscription?.unsubscribe(); };
  }, []);

  // Paste detection: opt-in prompt when clipboard has a URL
  useEffect(() => {
    let timer: any;
    const detect = async () => {
      try {
        const text = await navigator.clipboard.readText();
        const url = (text || '').trim();
        if (/^https?:\/\//i.test(url)) {
          setClipboardUrl(url);
          setShowPastePrompt(true);
        }
      } catch {}
    };
    // wait for a small idle time and only after a user has interacted
    const onFirstInteraction = () => {
      window.removeEventListener('pointerdown', onFirstInteraction);
      timer = setTimeout(detect, 1200);
    };
    window.addEventListener('pointerdown', onFirstInteraction, { once: true });
    return () => { window.removeEventListener('pointerdown', onFirstInteraction); timer && clearTimeout(timer); };
  }, []);

  useEffect(() => {
    if (session?.user) {
      fetchUserProfile(session.user.id);
      fetchVideos(session.user.id);
    } else {
      fetchVideos(null);
      setUserProfile(null);
    }
  }, [session?.user?.id]);

  const fetchUserProfile = async (userId: string) => {
    const { data, error } = await supabase.from('users').select('*').eq('id', userId).maybeSingle();
    if (error && error.code !== 'PGRST116') { console.error('Error fetching user profile:', error); return; }
    if (!data) {
      const { data: newUser, error: insertError } = await supabase.from('users').insert([{ id: userId, email: session?.user?.email || null }]).select().single();
      if (insertError) { console.error('Error creating new user profile:', insertError); return; }
      setUserProfile(newUser as UserProfileType); return;
    }
    setUserProfile(data as UserProfileType);
  };

  const fetchVideos = async (userId: string | null) => {
    setIsLoading(true);
    try {
    let q = supabase.from('videos').select('*', { count: 'none' }).order('created_at', { ascending: false });
      q = userId ? q.eq('user_id', userId) : q.limit(18);
      const { data, error } = await q;
      if (error) throw error;
      const norm = (Array.isArray(data) ? data : []).map(normalizeFromDb);
      setVideos(norm);
      setFilteredVideos(norm);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching videos:', e);
      enqueueToast('Error cargando videos.', 'error');
    } finally { setIsLoading(false); }
  };


  // Helpers
  const debounce = (fn: (...args: any[]) => void, ms = 300) => {
    let t: any; return (...args: any[]) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };

  // Sanitize free-text used inside PostgREST or() grammar to avoid breaking the filter when term contains commas/parentheses
  const sanitizeSearchTerm = (raw: string) => {
    try {
      let s = (raw || '').toString();
      // trim and collapse whitespace
      s = s.trim().replace(/\s+/g, ' ');
      // remove characters that conflict with or() grammar or wildcards
      // commas and parentheses break the comma-separated or list; quotes can confuse parsing
      s = s.replace(/[(),"']/g, '');
      // remove control characters
      s = s.replace(/[\u0000-\u001F\u007F]/g, '');
      // cap length
      if (s.length > 200) s = s.slice(0, 200);
      return s;
    } catch { return ''; }
  };

  // Core search executor used by both debounced flow and Enter/click
  const doSearch = React.useCallback(async (term: string, f: SearchFilters, opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    const loggedIn = Boolean(session?.user);
    const isDefaultFilters = f.platform === 'all' && f.cuisine === 'all' && !f.location;
    const safe = sanitizeSearchTerm(term);
    const hasQuery = Boolean(safe);
    const shouldQueryDB = loggedIn && (hasQuery || !isDefaultFilters);
    const shouldFilterLocal = !loggedIn && (hasQuery || !isDefaultFilters);

    // If nothing to filter/search, just mirror full list without toasts/loading
    if (!shouldQueryDB && !shouldFilterLocal) {
      setFilteredVideos(videos);
      return;
    }

    try {
      // Avoid flashing the UI during background (debounced) searches
      if (!silent) setIsLoading(true);
      if (shouldQueryDB) {
      let q = supabase.from('videos').select('*', { count: 'none' }).order('created_at', { ascending: false }).eq('user_id', session!.user.id);
        if (hasQuery) {
          q = q.or(`title.ilike.%${safe}%,restaurant_name.ilike.%${safe}%,restaurant_cuisine.ilike.%${safe}%`);
        }
        if (f.platform !== 'all') q = q.eq('platform', f.platform);
        if (f.cuisine !== 'all') q = q.eq('restaurant_cuisine', f.cuisine);
        if (f.location) q = q.ilike('restaurant_location', `%${f.location}%`);
        const { data, error } = await q;
        if (error) throw error;
        const norm = (Array.isArray(data) ? data : []).map(normalizeFromDb);
        setFilteredVideos(norm);
        const shouldToast = !opts?.silent; // only toast on explicit searches
        if (shouldToast) enqueueToast(`Se encontraron ${data.length} videos.`, 'info');
      } else {
        const safeLocalTerm = safe.toLowerCase();
        const local = videos.filter(v =>
          (v.title || '').toLowerCase().includes(safeLocalTerm) ||
          (v.restaurant_name || '').toLowerCase().includes(safeLocalTerm) ||
          (v.restaurant_cuisine || '').toLowerCase().includes(safeLocalTerm)
        );
        setFilteredVideos(local);
        const shouldToast = !opts?.silent;
        if (shouldToast) enqueueToast(`Se encontraron ${local.length} videos.`, 'info');
      }
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error('Error searching videos:', e);
      if (!opts?.silent) enqueueToast('Error al buscar videos.', 'error');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [session?.user, videos]);

  const debounced = useMemo(() => debounce((t: string, f: SearchFilters, o?: { silent?: boolean }) => { doSearch(t, f, o); }, 400), [doSearch]);
  const firstRunRef = useRef(true);
  useEffect(() => {
    // Skip the very first automatic run unless we're on /map with actual params
    const hasParams = Boolean(searchTerm || filters.platform !== 'all' || filters.cuisine !== 'all' || filters.location);
    if (firstRunRef.current) {
      firstRunRef.current = false;
      if (!(location.pathname === '/map' && hasParams)) {
        return;
      }
    }
    // If the input looks like a URL, don't trigger a search while the user is trying to add a link
    if (/^https?:\/\//i.test((searchTerm || '').trim())) {
      return;
    }
    debounced(searchTerm, filters, { silent: true });
  }, [searchTerm, filters, debounced]);
  const handleSearch = React.useCallback((t: string, f: SearchFilters) => { doSearch(t, f, { silent: false }); }, [doSearch]);
  const handleFilterChange = (next: SearchFilters) => setFilters(next);

  // Local settings snapshot
  const getLocalSettings = () => { try { return JSON.parse(localStorage.getItem('fs:settings:v1') || '{}'); } catch { return {}; } };

  // New handleAddVideo using the hardened flow with oEmbed gate and confirmation
  const handleAddVideo = async (videoUrl: string) => {
    if (!session?.user) { 
      enqueueToast('Debes iniciar sesión para añadir videos.', 'info'); 
      throw new Error('Usuario no autenticado.'); 
    }

    resetAddVideo(); // Clear any previous states
    
    try {
      const result = await addVideo(videoUrl, {
        // Pass user location if available for Places bias
        biasLatLng: userLoc ? { lat: userLoc.latitude, lng: userLoc.longitude } : undefined,
      });

      if (result.status === 'accepted') {
        // Video was accepted automatically - convert to our Video format and save
        const { data } = result;
        const videoToInsert = convertApiResponseToVideo(data, session.user.id);
        await saveVideoToDatabase(videoToInsert);
        
      } else if (result.status === 'needs_confirmation') {
        // Will be handled by ConfirmPlaceSheet component below
        return;
        
      } else if (result.status === 'oembed_required') {
        // Will be handled by DiagnosticsBanner component below
        return;
        
      } else {
        throw new Error(result.message || 'Error desconocido');
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error('Error al añadir video:', error);
      enqueueToast(`Error al añadir video: ${error.message || 'desconocido'}`, 'error');
      throw error;
    }
  };

  // Convert new API response to legacy Video format
  const convertApiResponseToVideo = (data: any, userId: string): Video => {
    const best = data.best;
    const oembed = data.oembed;
    
    // Debug: Log the data structure
    console.log('convertApiResponseToVideo - data:', data);
    console.log('convertApiResponseToVideo - oembed:', oembed);
    console.log('convertApiResponseToVideo - signals:', data.signals);
    
    // Use the server-provided original URL (now included in response)
    const originalUrl = data.originalVideoUrl || oembed?.url || '';
    const detectedPlatform = detectPlatformFromUrl(originalUrl);
    
    console.log('convertApiResponseToVideo - originalUrl:', originalUrl);
    console.log('convertApiResponseToVideo - detectedPlatform:', detectedPlatform);
    
    // Ensure platform is one of the allowed values (must match database constraint)
    const validPlatforms = ['YouTube', 'TikTok', 'Instagram', 'Facebook', 'Other'];
    const safePlatform = validPlatforms.includes(detectedPlatform) ? detectedPlatform : 'Other';
    
    function cuisineFromTypes(types?: string[]): string | null {
      if (!Array.isArray(types) || types.length === 0) return null;
      const t = types.map(s => String(s || '').toLowerCase()).join(' ');
      const checks: Array<[RegExp, string]> = [
        [/ramen|noodle/, 'Ramen'],
        [/sushi/, 'Sushi'],
        [/izakaya/, 'Izakaya'],
        [/japanese/, 'Japanese'],
        [/mexican|taco|taqueria/, 'Mexican'],
        [/italian|pizza|pizzeria|trattoria/, 'Italian'],
        [/thai/, 'Thai'],
        [/korean|bbq.*korean/, 'Korean'],
        [/chinese|dimsum|dim_sum/, 'Chinese'],
        [/indian|curry|tandoori/, 'Indian'],
        [/mediterranean|greek|lebanese|turkish/, 'Mediterranean'],
        [/seafood|mariscos/, 'Seafood'],
        [/steak|chophouse/, 'Steakhouse'],
        [/burger|hamburg/, 'Burgers'],
        [/barbecue|bbq/, 'Barbecue'],
        [/cafe|coffee_shop|coffee/, 'Cafe'],
        [/bakery|pasteleria|panaderia|patisserie/, 'Bakery'],
        [/dessert|ice_cream|gelato/, 'Dessert'],
      ];
      const labels: string[] = [];
      for (const [re, label] of checks) {
        if (re.test(t) && !labels.includes(label)) labels.push(label);
        if (labels.length >= 3) break;
      }
      return labels.length ? labels.join(', ') : null;
    }

    function priceFromLevel(level?: number | null): string | null {
      if (level == null) return null;
      return ['$', '$$', '$$$', '$$$$'][Math.min(Math.max(level, 0), 3)] || '$';
    }

    return {
      platform: safePlatform as any,
      video_url: originalUrl || '',
      thumbnail_url: oembed?.thumbnail_url || 'N/A',
      title: oembed?.title || 'N/A',
      creator_name: oembed?.author_name || 'N/A',
      views: 0,
      likes: 0,
      restaurant_name: best?.name || 'N/A',
      restaurant_cuisine: cuisineFromTypes((best as any)?.types) || 'N/A',
      restaurant_location: extractCityFromAddress(best?.formatted_address) || 'N/A',
      restaurant_address: best?.formatted_address || 'N/A',
      restaurant_rating: best?.rating || 0,
      restaurant_price_range: priceFromLevel((best as any)?.price_level) || null,
      google_maps_url: best?.place_id ? `https://maps.google.com/?q=place_id:${best.place_id}` : 'N/A',
      // Persist server-selected Place ID and hours to avoid client re-search mismatches
      enrich_place_id: (best as any)?.place_id || null,
      opening_hours_weekday_text: (best as any)?.opening_hours || null,
      extracted_dish_name: 'N/A',
      extracted_ingredients: [],
      extracted_estimated_price: 0,
      user_id: userId,
      embed_html: oembed?.html || null,
    };
  };

  // Comprehensive background enrichment - uses existing full pipeline
  const enrichVideoComprehensively = async (video: Video) => {
    try {
      console.log('🔄 Starting comprehensive enrichment (OCR + AI + Places) for:', video.restaurant_name);
      
      // Use the existing comprehensive enrichment function
      const enriched = await enrichVideoWithPlaces(video, { 
        minConfidence: 0.3,
        // Don't run expensive operations if we already have good data
        signal: undefined 
      });
      
      if (enriched === video) {
        console.log('📝 Video already fully enriched, skipping');
        return;
      }
      
      console.log('💾 Updating database with enriched data');
      setAddingProgress(85);
      setAddingText('Guardando datos enriquecidos...');
      
      // Extract only the fields that were enriched
      const updateData: any = {};
      
      // Core enrichment fields
      if (enriched.restaurant_rating !== video.restaurant_rating) updateData.restaurant_rating = enriched.restaurant_rating;
      if (enriched.restaurant_cuisine !== video.restaurant_cuisine) updateData.restaurant_cuisine = enriched.restaurant_cuisine;
      if (enriched.restaurant_address !== video.restaurant_address) updateData.restaurant_address = enriched.restaurant_address;
      if (enriched.google_maps_url !== video.google_maps_url) updateData.google_maps_url = enriched.google_maps_url;
      
      // Enrichment metadata
      if ((enriched as any).places_photo_url) updateData.places_photo_url = (enriched as any).places_photo_url;
      if ((enriched as any).enrich_place_id) updateData.enrich_place_id = (enriched as any).enrich_place_id;
      if ((enriched as any).enrich_confidence) updateData.enrich_confidence = (enriched as any).enrich_confidence;
      if ((enriched as any).opening_hours_weekday_text) updateData.opening_hours_weekday_text = (enriched as any).opening_hours_weekday_text;
      
      // Only update if there are changes
      if (Object.keys(updateData).length === 0) {
        console.log('📝 No new enrichment data to save');
        return;
      }
      
      // Update database
      const { error } = await supabase
        .from('videos')
        .update(updateData)
        .eq('id', video.id);
        
      if (error) {
        console.error('❌ Error updating video with enriched data:', error);
        return;
      }
      
      // Update local state immediately
      const updatedVideo = { ...video, ...updateData };
      setVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      setFilteredVideos(prev => prev.map(v => v.id === video.id ? updatedVideo : v));
      
      setAddingProgress(100);
      setAddingText('¡Completado!');
      
      console.log('✅ Video comprehensively enriched!', {
        name: enriched.restaurant_name,
        rating: enriched.restaurant_rating,
        address: enriched.restaurant_address,
        confidence: (enriched as any).enrich_confidence,
        fieldsUpdated: Object.keys(updateData)
      });
      
    } catch (error) {
      console.error('❌ Comprehensive enrichment failed:', error);
      // Silent fail - don't disrupt user experience
    }
  };

  // Save video to database
  const saveVideoToDatabase = async (videoToInsert: Video) => {
    const { data: saved, error: saveErr } = await supabase.from('videos').insert([videoToInsert]).select();
    if (saveErr) {
      const isDup = (saveErr as any)?.code === '23505' || /duplicate key value/i.test((saveErr as any)?.message || '');
      if (isDup) {
        // Fetch existing record for this user
        const { data: existing } = await supabase
          .from('videos')
          .select('*')
          .eq('video_url', videoToInsert.video_url)
          .eq('user_id', session.user.id)
          .maybeSingle();
        if (existing) {
          const added = existing as Video;
          setVideos(prev => [added, ...prev.filter(v => v.id !== added.id)]);
          setFilteredVideos(prev => [added, ...prev.filter(v => v.id !== added.id)]);
          enqueueToast('Ya lo tenías guardado. Actualizado en la lista.', 'info');
          return;
        }
      }
      throw saveErr;
    }
    const added = normalizeFromDb(saved![0]);
    setVideos(prev => [added, ...prev]);
    setFilteredVideos(prev => [added, ...prev]);
    enqueueToast('Añadido!', 'success');
    
          // 🚀 Automatic background enrichment - use existing comprehensive pipeline
      if (added.restaurant_name && added.restaurant_name !== 'N/A') {
        console.log('🔄 Starting comprehensive enrichment for:', added.restaurant_name);
        // Show enrichment progress
        setAddingActive(true);
        setAddingProgress(60);
        setAddingText('Enriqueciendo con IA y Google Places...');
        
        enrichVideoComprehensively(added).finally(() => {
          setAddingActive(false);
          setAddingProgress(0);
          setAddingText('');
        });
      }
  };

  // Handle place confirmation from ConfirmPlaceSheet
  const handlePlaceConfirm = async (place: RankedCandidate) => {
    if (!session?.user || !confirm) return;
    
    try {
      // Create video with the confirmed place, including original video URL
      const videoToInsert = convertApiResponseToVideo({
        best: place,
        oembed: confirm.oembed,
        signals: confirm.signals,
        originalVideoUrl: confirm.originalVideoUrl || confirm.oembed?.url || '', // Pass the original URL
      }, session.user.id);
      
      await saveVideoToDatabase(videoToInsert);
      resetAddVideo(); // Clear confirmation state
    } catch (error: any) {
      console.error('Error saving confirmed video:', error);
      enqueueToast(`Error al guardar: ${error.message || 'desconocido'}`, 'error');
    }
  };

  // Keyboard shortcut for diagnostics (Ctrl+D)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setDiagnosticsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);



  // Helper functions
  const detectPlatformFromUrl = (url: string): string => {
    try {
      const h = new URL(url).hostname.toLowerCase();
      if (h.includes('youtube') || h === 'youtu.be') return 'YouTube';
      if (h.includes('tiktok')) return 'TikTok';
      if (h.includes('instagram') || h.includes('instagr.am')) return 'Instagram';
      if (h.includes('facebook') || h.includes('fb.watch')) return 'Facebook';
    } catch {}
    return 'Other';
  };

  const extractCityFromAddress = (address?: string): string | null => {
    if (!address) return null;
    const parts = address.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length === 0) return null;

    const isCountry = (s: string) => /^(mexico|méxico|united states|usa|canada)$/i.test(s);
    const isState = (s: string) => /^(jal\.?|jalisco|cdmx|ciudad de méxico|nuevo león|edomex|estado de méxico|ny|ca|tx)$/i.test(s);
    const cleaned = parts.filter((p) => !isCountry(p) && !isState(p));

    for (const seg of cleaned) {
      const m = seg.match(/^(\d{4,6})\s+(.+)$/);
      if (m) return m[2].trim(); // drop postal code, keep city text
    }

    const longText = cleaned.find((p) => /[A-Za-zÁ-ÿ]{3,}/.test(p) && !/\d{3,}/.test(p));
    if (longText) return longText;

    // fallback: prefer last non-country/state
    return cleaned[cleaned.length - 1] || parts[parts.length - 1] || parts[0];
  };

  // Legacy fallback functionality is maintained through the existing handleRetryAfterFix function

  const handleRetryAfterFix = async (fields: { restaurant_name: string; restaurant_location: string; title?: string }) => {
    if (!session?.user) return;
    try {
      setFixOpen(false);
      const base: any = {
        platform: 'Other', video_url: fixUrl, thumbnail_url: 'N/A', title: fields.title || 'N/A', creator_name: 'N/A',
        views: 0, likes: 0, restaurant_name: fields.restaurant_name || 'N/A', restaurant_cuisine: 'N/A',
        restaurant_location: fields.restaurant_location || 'N/A', restaurant_address: 'N/A', restaurant_rating: 0,
        restaurant_price_range: 'N/A', google_maps_url: 'N/A', extracted_dish_name: 'N/A', extracted_ingredients: [], extracted_estimated_price: 0,
      };
      let enriched = base; try {
        const ls = getLocalSettings(); const radiusKm = typeof ls.radius_km === 'number' ? ls.radius_km : 10;
  enriched = await enrichVideoWithPlaces(base, { radius: radiusKm * 1000, minConfidence: 0.7 });
      } catch {}
  const { id, ...rest } = enriched;
      const ls2: any = getLocalSettings();
      const videoToInsert = sanitizeForInsert({
        ...rest,
        user_id: session.user.id,
        visibility: ls2?.default_visibility || 'private',
        share_options: ls2?.share_options || {},
      } as any);
      const { data, error } = await supabase.from('videos').insert([videoToInsert]).select();
      if (error) {
        const isDup = (error as any)?.code === '23505' || /duplicate key value/i.test((error as any)?.message || '');
        if (isDup) {
          const { data: existing } = await supabase
            .from('videos')
            .select('*')
            .eq('video_url', (videoToInsert as any).video_url)
            .eq('user_id', session.user.id)
            .maybeSingle();
          if (existing) {
            const addedVideo = existing as Video;
            setVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !localStubIdsRef.current.has(String(v.id)))]);
            setFilteredVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !localStubIdsRef.current.has(String(v.id)))]);
            enqueueToast('Este video ya existía. Usando el existente.', 'info');
            return;
          }
        }
        throw error;
      }
      const addedVideo = data![0] as Video;
      setVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !localStubIdsRef.current.has(String(v.id)))]);
      setFilteredVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !localStubIdsRef.current.has(String(v.id)))]);
      enqueueToast('Reintento exitoso: video añadido!', 'success');
    } catch (e: any) { enqueueToast(`No se pudo reintentar: ${e?.message || 'desconocido'}`, 'error'); setFixOpen(false); }
  };

  // Confirm-before-save action
  const handleConfirmSave = async (fields: any) => {
    if (!session?.user) return;
    try {
      const ls: any = getLocalSettings();
      const videoToInsert = sanitizeForInsert({
        ...fields,
        user_id: session.user.id,
        visibility: ls?.default_visibility || 'private',
        share_options: ls?.share_options || {},
      } as any);
      const { data, error } = await supabase.from('videos').insert([videoToInsert]).select();
      if (error) {
        const isDup = (error as any)?.code === '23505' || /duplicate key value/i.test((error as any)?.message || '');
        if (isDup) {
          const { data: existing } = await supabase
            .from('videos')
            .select('*')
            .eq('video_url', (videoToInsert as any).video_url)
            .eq('user_id', session.user.id)
            .maybeSingle();
          if (existing) {
            const addedVideo = existing as Video;
            setVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !String(v.id).startsWith('local_'))]);
            setFilteredVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !String(v.id).startsWith('local_'))]);
            enqueueToast('Ya lo tenías guardado. Mostrando el existente.', 'info');
            return;
          }
        }
        throw error;
      }
      const addedVideo = data![0] as Video;
      setVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !String(v.id).startsWith('local_'))]);
      setFilteredVideos(prev => [addedVideo, ...prev.filter(v => v.id !== addedVideo.id && !String(v.id).startsWith('local_'))]);
      enqueueToast('Guardado!', 'success');
    } catch (e: any) {
      enqueueToast(`No se pudo guardar: ${e?.message || 'desconocido'}`, 'error');
    }
  };

  // Bulk add handling
  const queueBulk = async (urls: string[]) => {
    setBulkOpen(false);
    setBulkItems(urls.map((u) => ({ url: u, status: 'Queued' })));
    setBulkProgressOpen(true);
    for (let i = 0; i < urls.length; i++) {
      const u = urls[i];
      const setStatus = (status: string, message?: string) => setBulkItems(prev => prev.map((it, idx) => idx === i ? { ...it, status, message } : it));
      try {
        setStatus('Extracting');
        await handleAddVideo(u);
        setStatus('Enriched');
        setStatus('Saved');
      } catch (e: any) {
        setStatus('Needs review', e?.message);
      }
    }
  };

  const handleVideoClick = (video: Video) => { setSelectedVideo(video); setIsModalOpen(true); setHighlightedVideoId(video.id || null); };
  const closeModal = () => { setIsModalOpen(false); setSelectedVideo(null); };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      const { error } = await supabase.from('videos').delete().eq('id', videoId);
      if (error) throw error;
      setVideos(prev => prev.filter(v => v.id !== videoId));
      setFilteredVideos(prev => prev.filter(v => v.id !== videoId));
      enqueueToast('Video eliminado.', 'success');
    } catch (e: any) { enqueueToast(`Error eliminando: ${e?.message || 'desconocido'}`, 'error'); }
  };

  const handleSignOut = async () => { await supabase.auth.signOut(); };
  const handleViewProfile = () => setShowProfile(true);
  const handleGoHome = () => setShowProfile(false);

  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <AuthForm onAuthSuccess={() => { /* session effect will update */ }} />
      </div>
    );
  }

  // simple responsive hook
  const useIsLargeScreen = () => {
    const [isLg, setIsLg] = useState<boolean>(false);
    useEffect(() => {
      const mq = window.matchMedia('(min-width: 1024px)');
      const apply = () => setIsLg(mq.matches);
      apply();
      mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply as any);
      return () => { mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply as any); };
    }, []);
    return isLg;
  };

  const Content: React.FC = () => {
  const { settings } = useSettings();
  const isLg = useIsLargeScreen();

    const applyDefaults = (arr: Video[]) => {
      const platforms = settings.platforms || [];
      let list = Array.isArray(arr) ? [...arr] : [];
      if (platforms.length) list = list.filter(v => platforms.includes((v.platform as any)) || v.platform === 'Other');
      switch (settings.default_sort) {
        case 'rating': list.sort((a,b) => (b.restaurant_rating || 0) - (a.restaurant_rating || 0)); break;
        case 'popular': list.sort((a,b) => ((b.views||0)+(b.likes||0)) - ((a.views||0)+(a.likes||0))); break;
        case 'recent': list.sort((a,b) => new Date(b.created_at||0).getTime() - new Date(a.created_at||0).getTime()); break;
        default: break;
      }
      return list;
    };

    const visibleVideos = applyDefaults(filteredVideos);

    const onCardClick = (v: Video) => {
      setSelectedVideo(v);
      setHighlightedVideoId(v.id || null);
      // Always open centered modal for details
      setIsModalOpen(true);
    };

  const MainSection = (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
  {/* Using a single main SearchBar below; removed top GlobalSearch */}
        {settings.layout === 'map' && (
          <div className="mb-6">
            <VideoMap
              videos={visibleVideos}
              highlightedVideoId={highlightedVideoId}
              onMarkerClick={(id) => {
                setHighlightedVideoId(id);
                const el = itemRefs.current.get(id);
                if (el && 'scrollIntoView' in el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const v = visibleVideos.find(v => v.id === id);
                if (v) onCardClick(v);
              }}
            />
          </div>
        )}
  <div>
          <Header
            userEmail={session?.user?.email || null}
            onSignOut={handleSignOut}
            onViewProfile={handleViewProfile}
            onOpenSettings={() => setSettingsOpen(true)}
          />

          {/* Filters handled within SearchBar; removed extra external filters button. */}
          {showProfile && userProfile ? (
            <UserProfile user={userProfile} onVideoClick={onCardClick} onGoHome={handleGoHome} />
          ) : (
            <>
              <SearchBar
                searchTerm={searchTerm}
                filters={filters}
                onSearchTermChange={setSearchTerm}
                onFiltersChange={setFilters}
                onSearch={handleSearch}
                onAddVideo={handleAddVideo}
                addingActive={addVideoLoading || addingActive}
              />
              {/* Mobile Paste FAB */}
              <PasteFab onPaste={(url)=>handleAddVideo(url)} />
              {/* Paste detection prompt */}
              {showPastePrompt && clipboardUrl && (
                <AddLinkPrompt url={clipboardUrl} onAdd={(u)=>{ setShowPastePrompt(false); handleAddVideo(u); }} onDismiss={()=>setShowPastePrompt(false)} />
              )}
              {/* Bulk add button */}
              <div className="mb-2">
                <button className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-700" onClick={()=>setBulkOpen(true)}>Bulk add</button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {searchTerm && (<span className="px-3 py-1 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-full">“{searchTerm}”</span>)}
                {filters.platform !== 'all' && (<span className="px-3 py-1 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-full">{filters.platform}</span>)}
                {filters.cuisine !== 'all' && (<span className="px-3 py-1 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full">{filters.cuisine}</span>)}
                {filters.location && (<span className="px-3 py-1 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-full">{filters.location}</span>)}
                {(searchTerm || filters.platform !== 'all' || filters.cuisine !== 'all' || filters.location) && (
                  <button type="button" className="ml-auto px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full border border-gray-200" onClick={() => { setSearchTerm(''); setFilters({ platform: 'all', cuisine: 'all', location: '' }); }}>Resetear filtros</button>
                )}
              </div>
              <VideoGrid
                videos={visibleVideos}
                onVideoClick={onCardClick}
                onDeleteVideo={handleDeleteVideo}
                isLoading={isLoading}
                currentUserId={session?.user?.id || null}
                onAddVideo={handleAddVideo}
                highlightedVideoId={highlightedVideoId}
                registerItemRef={(id, el) => { itemRefs.current.set(id, el); }}
              />
            </>
          )}
          {/* Centered details modal for all viewports */}
          <VideoModal video={selectedVideo} isOpen={isModalOpen} onClose={closeModal} />
  </div>
  {/* Removed external Filters modal; SearchBar contains filters. */}

        {/* Mobile bottom navigation */}
        <div className="lg:hidden"><BottomTabs /></div>
  {/* Onboarding coach marks */}
  <OnboardingCoach suppress={isModalOpen} />
        {/* Modals */}
        <BulkAddDialog open={bulkOpen} onClose={()=>setBulkOpen(false)} onSubmit={queueBulk} />
  <ConfirmExtractDialog open={confirmOpen} draft={confirmDraft} onCancel={()=>{ setConfirmOpen(false); setAddingActive(false); setAddingProgress(0); setAddingText(''); }} onConfirm={(fields)=>{ setConfirmOpen(false); setAddingActive(true); setAddingText('Guardando…'); setAddingProgress(90); handleConfirmSave(fields).finally(()=>{ setAddingActive(false); setAddingProgress(0); setAddingText(''); }); }} />
  <BulkProgressDrawer open={bulkProgressOpen} items={bulkItems} onClose={()=>setBulkProgressOpen(false)} />
      </div>
    );

  // Always use the centered modal for details.
  return MainSection;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <SettingsProvider userId={session?.user?.id || null}>
        <Content />
        {(addingActive || addVideoLoading) && (
          <div className="fixed inset-x-0 top-0 z-[9999] pointer-events-none">
            <div className="mx-auto max-w-3xl mt-3 px-4">
              <div className="rounded-xl bg-white/95 backdrop-blur-sm shadow-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3">
                  <StepBar
                    step={(() => {
                      if (addVideoLoading) return 1; // OCR step during loading
                      if (addingActive && addingProgress > 80) return 5; // Save step
                      if (addingActive && addingProgress > 50) return 4; // Enrich step
                      if (addingActive) return 3; // Extract step completed, enriching
                      return 0; // oEmbed completed
                    })()}
                    labels={['oEmbed', 'Transcribir', 'OCR', 'Extraer', 'Enriquecer', 'Guardar']}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <SettingsDrawer open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        <ExtractionFixDialog isOpen={fixOpen} url={fixUrl} errorMessage={fixError} onCancel={() => setFixOpen(false)} onRetry={handleRetryAfterFix} />
        
        {/* New hardened video flow components */}
        {oembedError && (
          <DiagnosticsBanner 
            error={oembedError} 
            onDismiss={() => resetAddVideo()} 
          />
        )}
        
        {confirm && (
          <ConfirmPlaceSheet
            open={true}
            candidates={confirm.top || []}
            onConfirm={handlePlaceConfirm}
            onCancel={() => resetAddVideo()}
          />
        )}
        
        {diagnosticsOpen && (
          <SupabaseDiagnostics onClose={() => setDiagnosticsOpen(false)} />
        )}
      </SettingsProvider>
    </div>
  );
};

const App: React.FC = () => (
  <ToastProvider>
    <Router>
      <Routes>
        <Route path="/" element={<MainApp />} />
  <Route path="/map" element={<MainApp />} />
  <Route path="/lists" element={<ListsHub />} />
  <Route path="/profile" element={<ProfilePage />} />
        <Route path="/list/:listId" element={<SharedListPageWrapper />} />
  <Route path="/find" element={<FindRestaurantCard />} />
  <Route path="/admin" element={<AdminDashboard />} />
      </Routes>
    </Router>
  </ToastProvider>
);

export default App;